import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  MarkerType,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getSmoothStepPath,
  useInternalNode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  SWIMLANE_HEIGHT,
  combinedProcessDisplayNodeTypes,
} from './combinedProcessFlowchartDisplayNodes';
import FlowchartSubprocessLinkOverlay from '../flowchart/FlowchartSubprocessLinkOverlay';
import FlowchartLegend from '../flowchart/FlowchartLegend';
import { fetchFlowchartSubprocessLinks } from '../../request/flowchart/subprocessLinks.api';
import { getFloatingEdgeParamsForNodes } from '../../features/flowchart/floatingEdgeGeometry.ts';

function actorName(node) {
  return String(node?.actor || '').trim();
}

function normalizeNodeDocuments(documentsFromApi) {
  if (!Array.isArray(documentsFromApi) || documentsFromApi.length === 0) return [];
  const out = [];
  for (const d of documentsFromApi) {
    if (!d || typeof d !== 'object') continue;
    const id = typeof d.id === 'string' ? d.id.trim() : '';
    const name = typeof d.name === 'string' ? d.name.trim() : '';
    if (!id && !name) continue;
    const rawType = d.docType ?? d.doc_type ?? null;
    let docType = null;
    if (typeof rawType === 'string') {
      const trimmed = rawType.trim();
      docType = trimmed === '' ? null : trimmed;
    }
    out.push({
      id,
      name,
      docType,
      systems: Array.isArray(d.systems)
        ? d.systems
            .map((s) => ({
              id: typeof s?.id === 'string' ? s.id.trim() : '',
              name: typeof s?.name === 'string' ? s.name.trim() : '',
            }))
            .filter((s) => s.id && s.name)
        : [],
    });
  }
  return out;
}

function getEdgeMarkers(isBidirectional = false) {
  const marker = {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#000000',
  };
  return {
    markerEnd: marker,
    markerStart: isBidirectional ? marker : undefined,
  };
}

function wpDist(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function wpNormalize(dx, dy) {
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

function buildWaypointPath(points, radius = 10) {
  if (!Array.isArray(points) || points.length < 2) return '';
  if (points.length === 2) return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const r = Math.min(radius, wpDist(prev, curr) / 2, wpDist(curr, next) / 2);
    const v1 = wpNormalize(curr.x - prev.x, curr.y - prev.y);
    const v2 = wpNormalize(next.x - curr.x, next.y - curr.y);
    const p1 = { x: curr.x - v1.x * r, y: curr.y - v1.y * r };
    const p2 = { x: curr.x + v2.x * r, y: curr.y + v2.y * r };
    d += ` L ${p1.x},${p1.y} Q ${curr.x},${curr.y} ${p2.x},${p2.y}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
}

const SIDE_TO_POSITION = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

function CombinedReadOnlyEdge({
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  targetHandleId,
  style = {},
  markerStart,
  markerEnd,
  data,
  label,
}) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const needFloating = !sourceHandleId || !targetHandleId;
  const floatingParams = needFloating ? getFloatingEdgeParamsForNodes(sourceNode, targetNode) : null;
  const sx = sourceHandleId ? sourceX : floatingParams ? floatingParams.sx : sourceX;
  const sy = sourceHandleId ? sourceY : floatingParams ? floatingParams.sy : sourceY;
  const tx = targetHandleId ? targetX : floatingParams ? floatingParams.tx : targetX;
  const ty = targetHandleId ? targetY : floatingParams ? floatingParams.ty : targetY;
  const sourcePos = sourceHandleId
    ? sourcePosition
    : floatingParams
      ? SIDE_TO_POSITION[floatingParams.sourcePos] || sourcePosition
      : sourcePosition;
  const targetPos = targetHandleId
    ? targetPosition
    : floatingParams
      ? SIDE_TO_POSITION[floatingParams.targetPos] || targetPosition
      : targetPosition;

  const waypoints = Array.isArray(data?.waypoints)
    ? data.waypoints.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
    : [];
  const edgeLabel = data?.label || label || '';

  let edgePath;
  let labelX;
  let labelY;
  if (waypoints.length > 0) {
    const pts = [{ x: sx, y: sy }, ...waypoints, { x: tx, y: ty }];
    edgePath = buildWaypointPath(pts);
    const mid = pts[Math.floor(pts.length / 2)];
    labelX = mid.x;
    labelY = mid.y;
  } else {
    const [p, lx, ly] = getSmoothStepPath({
      sourceX: sx,
      sourceY: sy,
      sourcePosition: sourcePos,
      targetX: tx,
      targetY: ty,
      targetPosition: targetPos,
      borderRadius: 14,
    });
    edgePath = p;
    labelX = lx;
    labelY = ly;
  }

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={{ stroke: '#000000', strokeWidth: 2, ...(style || {}) }}
      />
      {edgeLabel ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 12,
              fontWeight: 600,
              color: '#111827',
              background: '#ffffff',
              padding: '2px 6px',
              borderRadius: 4,
              pointerEvents: 'none',
            }}
          >
            {edgeLabel}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

const combinedProcessEdgeTypes = {
  custom: CombinedReadOnlyEdge,
};

function buildLaneOrder(nodesIn, swimlaneLayouts) {
  const present = [];
  const seen = new Set();
  for (const node of nodesIn) {
    const actor = actorName(node);
    if (!actor || seen.has(actor)) continue;
    seen.add(actor);
    present.push(actor);
  }
  const layoutOrder = (Array.isArray(swimlaneLayouts) ? swimlaneLayouts : [])
    .map((entry) => String(entry?.actorName || '').trim())
    .filter(Boolean);
  const presentSet = new Set(present);
  const kept = layoutOrder.filter((actor) => presentSet.has(actor));
  const keptSet = new Set(kept);
  return [...kept, ...present.filter((actor) => !keptSet.has(actor))];
}

function buildLaneGeometry(actors, heightByActor) {
  let cursor = 0;
  return actors.map((actor, index) => {
    const height = heightByActor[actor] || SWIMLANE_HEIGHT;
    const lane = { actor, index, height, yStart: cursor, yEnd: cursor + height };
    cursor += height;
    return lane;
  });
}

function toCombinedProcessFlow(flowchartData) {
  const nodesIn = Array.isArray(flowchartData?.nodes) ? flowchartData.nodes : [];
  const connectors = Array.isArray(flowchartData?.connectors) ? flowchartData.connectors : [];
  const swimlaneLayouts = Array.isArray(flowchartData?.swimlaneLayouts) ? flowchartData.swimlaneLayouts : [];
  const actors = buildLaneOrder(nodesIn, swimlaneLayouts);

  const heightByActor = {};
  swimlaneLayouts.forEach((entry) => {
    const actor = String(entry?.actorName || '').trim();
    const height = Number(entry?.swimlaneHeight);
    if (actor && Number.isFinite(height) && height > 0) heightByActor[actor] = height;
  });

  const hasSavedPosition = (node) => {
    const px = Number(node?.position?.x);
    const py = Number(node?.position?.y);
    return Number.isFinite(px) && Number.isFinite(py) && (px !== 0 || py !== 0);
  };
  const hasSavedPositions = nodesIn.some(hasSavedPosition);
  // Same slip-case as FlowchartViewer: if any node lacks coords, run ELK then
  // re-apply saved positions so missing nodes get layout instead of stacking at x=0.
  const anyMissingPosition = nodesIn.some((node) => !hasSavedPosition(node));
  const needsElk = !hasSavedPositions || anyMissingPosition;

  const xs = nodesIn.map((node) => Number(node?.position?.x)).filter((value) => Number.isFinite(value));
  const minX = (xs.length ? Math.min(...xs) : 0) - 200;
  const maxX = (xs.length ? Math.max(...xs) : 0) + NODE_WIDTH + 400;
  const swimlaneWidth = Math.max(maxX - minX, NODE_WIDTH + 400);

  const lanes = buildLaneGeometry(actors, heightByActor);
  const laneByActor = Object.fromEntries(lanes.map((lane) => [lane.actor, lane]));

  const swimlaneNodes = lanes.map((lane) => ({
    id: `combined-lane-${lane.index}-${lane.actor}`,
    type: 'swimlane',
    position: { x: minX, y: lane.yStart },
    data: {
      label: lane.actor,
      width: swimlaneWidth,
      height: lane.height,
      index: lane.index,
    },
    draggable: false,
    selectable: false,
    zIndex: 0,
  }));

  const nodes = nodesIn.map((node) => {
    const kind = String(node?.nodeType || '').toLowerCase();
    const type = kind === 'decision' ? 'decision' : kind === 'start' || kind === 'end' ? 'oval' : 'rectangle';
    const x = Number(node?.position?.x);
    const y = Number(node?.position?.y);
    const hasPersistedPosition = hasSavedPosition(node);
    let actor = actorName(node);
    if (!actor && (kind === 'start' || kind === 'end')) {
      if (kind === 'start') {
        const outgoing = connectors.find((connector) => String(connector.source) === String(node.id));
        const target = nodesIn.find((entry) => String(entry.id) === String(outgoing?.target));
        actor = actorName(target);
      } else {
        const incoming = connectors.find((connector) => String(connector.target) === String(node.id));
        const source = nodesIn.find((entry) => String(entry.id) === String(incoming?.source));
        actor = actorName(source);
      }
    }
    const isMapped = !!(actorName(node) || actor);
    const lane = actor ? laneByActor[actor] : null;

    let posX = hasPersistedPosition ? x : 0;
    let posY = 0;
    if (hasSavedPositions && hasPersistedPosition) {
      // Exact saved coordinates — same hydration as the actual flowchart page.
      posX = x;
      posY = y;
      // Clamp process-step Y into its lane when drifted (same as FlowchartViewer).
      if (lane && kind !== 'start' && kind !== 'end') {
        const LANE_INNER_PADDING = 12;
        const minY = lane.yStart + LANE_INNER_PADDING;
        const maxY = lane.yStart + lane.height - NODE_HEIGHT - LANE_INNER_PADDING;
        posY = maxY < minY ? minY : Math.min(Math.max(posY, minY), maxY);
      }
    } else if (lane) {
      posY = lane.yStart + (lane.height - NODE_HEIGHT) / 2;
    }

    return {
      id: String(node.id),
      type,
      data: {
        nodeDescription: node.nodeDescription || '',
        actor: actorName(node) || actor,
        nodeType: node.nodeType,
        system: node.system || '',
        systems: Array.isArray(node.systems) ? node.systems : [],
        soxControl: node.soxControl || '',
        document: node.document || '',
        documents: normalizeNodeDocuments(node.documents),
        keyControl: node.keyControl === true,
        isMapped,
        hasPersistedPosition,
        risks: Array.isArray(node.risks) ? node.risks : [],
        mappedControls: Array.isArray(node.mappedControls) ? node.mappedControls : [],
      },
      position: { x: posX, y: posY },
      draggable: false,
      selectable: false,
      zIndex: 1,
      style: {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        background: 'transparent',
        border: 'none',
        padding: 0,
      },
    };
  });

  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = connectors
    .filter((connector) => nodeIds.has(String(connector.source)) && nodeIds.has(String(connector.target)))
    .map((connector, index) => {
      const sourceNode = nodesIn.find((node) => String(node.id) === String(connector.source));
      const isDecision = String(sourceNode?.nodeType || '').toLowerCase() === 'decision';
      const edgeLabel = connector.label || '';
      let sourceHandle = connector.sourceHandle ?? undefined;
      const targetHandle = connector.targetHandle ?? undefined;
      if (!sourceHandle && isDecision) {
        if (edgeLabel.toLowerCase() === 'yes') sourceHandle = 'right';
        else if (edgeLabel.toLowerCase() === 'no') sourceHandle = 'bottom';
      }
      const isBidirectional = connector.isBidirectional === true;
      const waypoints = Array.isArray(connector.waypoints)
        ? connector.waypoints
            .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
            .map((p) => ({ x: p.x, y: p.y }))
        : [];
      const connectorId = connector.id || `connector_${connector.source}_${connector.target}_${index}`;
      return {
        id: connectorId,
        source: String(connector.source),
        target: String(connector.target),
        sourceHandle,
        targetHandle,
        type: 'custom',
        animated: false,
        ...getEdgeMarkers(isBidirectional),
        style: { stroke: '#000000', strokeWidth: 2 },
        data: {
          label: edgeLabel,
          connectorId,
          isBidirectional,
          waypoints,
        },
      };
    });

  return { nodes: [...swimlaneNodes, ...nodes], edges, lanes, heightByActor, needsElk };
}

async function applyElkLayout(nodes, edges, lanes) {
  const elk = new ELK();
  const contentNodes = nodes.filter((node) => node.type !== 'swimlane');
  const swimlaneNodes = nodes.filter((node) => node.type === 'swimlane');
  const laneByActor = Object.fromEntries(lanes.map((lane) => [lane.actor, lane]));

  const swimlaneGroups = {};
  const unmappedNodes = [];
  const startEndNodes = [];

  contentNodes.forEach((node) => {
    const kind = String(node?.data?.nodeType || '').toLowerCase();
    if (kind === 'start' || kind === 'end') {
      startEndNodes.push(node);
      return;
    }
    const actor = String(node?.data?.actor || '').trim();
    const lane = laneByActor[actor];
    if (lane) {
      if (!swimlaneGroups[lane.index]) swimlaneGroups[lane.index] = [];
      swimlaneGroups[lane.index].push(node);
    } else {
      unmappedNodes.push(node);
    }
  });

  unmappedNodes.forEach((node) => {
    const sourceEdge = edges.find((e) => e.target === node.id);
    if (sourceEdge) {
      const sourceNode = contentNodes.find((n) => n.id === sourceEdge.source);
      const sourceLane = laneByActor[String(sourceNode?.data?.actor || '').trim()];
      if (sourceLane) {
        if (!swimlaneGroups[sourceLane.index]) swimlaneGroups[sourceLane.index] = [];
        swimlaneGroups[sourceLane.index].push(node);
        return;
      }
    }
    if (lanes.length > 0) {
      if (!swimlaneGroups[0]) swimlaneGroups[0] = [];
      swimlaneGroups[0].push(node);
    }
  });

  const elkNodes = [];
  lanes.forEach((lane) => {
    (swimlaneGroups[lane.index] || []).forEach((node) => {
      elkNodes.push({
        id: node.id,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        properties: { swimlaneIndex: lane.index },
      });
    });
  });

  startEndNodes.forEach((node) => {
    let laneIndex = 0;
    const kind = String(node?.data?.nodeType || '').toLowerCase();
    if (kind === 'start') {
      const outEdge = edges.find((e) => e.source === node.id);
      const targetNode = outEdge ? contentNodes.find((n) => n.id === outEdge.target) : null;
      const lane = targetNode ? laneByActor[String(targetNode.data?.actor || '').trim()] : null;
      if (lane) laneIndex = lane.index;
    } else if (kind === 'end') {
      const inEdge = edges.find((e) => e.target === node.id);
      const sourceNode = inEdge ? contentNodes.find((n) => n.id === inEdge.source) : null;
      const lane = sourceNode ? laneByActor[String(sourceNode.data?.actor || '').trim()] : null;
      if (lane) laneIndex = lane.index;
    }
    elkNodes.push({
      id: node.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      properties: { swimlaneIndex: laneIndex },
    });
  });

  const addedIds = new Set(elkNodes.map((n) => n.id));
  contentNodes.forEach((node) => {
    if (!addedIds.has(node.id)) {
      elkNodes.push({ id: node.id, width: NODE_WIDTH, height: NODE_HEIGHT, properties: { swimlaneIndex: 0 } });
    }
  });

  const layoutedGraph = await elk.layout({
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '120',
      'elk.layered.spacing.nodeNodeBetweenLayers': '150',
      'elk.layered.spacing.edgeNodeBetweenLayers': '80',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    },
    children: elkNodes,
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  });

  const elkPositionById = {};
  layoutedGraph.children.forEach((elkNode) => {
    elkPositionById[elkNode.id] = elkNode;
  });

  const laneByNodeId = {};
  lanes.forEach((lane) => {
    (swimlaneGroups[lane.index] || []).forEach((node) => {
      laneByNodeId[node.id] = lane;
    });
  });
  startEndNodes.forEach((node) => {
    const elkNode = elkNodes.find((n) => n.id === node.id);
    const laneIndex = elkNode?.properties?.swimlaneIndex ?? 0;
    const lane = lanes[laneIndex];
    if (lane) laneByNodeId[node.id] = lane;
  });

  // FlowchartViewer: finalPosition = persisted || ELK. Keep saved coords; fill only missing.
  const updatedContentNodes = contentNodes.map((node) => {
    const elkNode = elkPositionById[node.id];
    const lane = laneByNodeId[node.id];
    const kind = String(node?.data?.nodeType || '').toLowerCase();

    if (node.data?.hasPersistedPosition) {
      let posX = node.position.x;
      let posY = node.position.y;
      if (lane && kind !== 'start' && kind !== 'end') {
        const LANE_INNER_PADDING = 12;
        const minY = lane.yStart + LANE_INNER_PADDING;
        const maxY = lane.yStart + lane.height - NODE_HEIGHT - LANE_INNER_PADDING;
        posY = maxY < minY ? minY : Math.min(Math.max(posY, minY), maxY);
      }
      return { ...node, position: { x: posX, y: posY } };
    }

    return {
      ...node,
      position: {
        x: typeof elkNode?.x === 'number' ? elkNode.x : node.position.x,
        y: lane
          ? lane.yStart + (lane.height - NODE_HEIGHT) / 2
          : typeof elkNode?.y === 'number'
            ? elkNode.y
            : node.position.y,
      },
    };
  });

  const allX = updatedContentNodes.map((n) => n.position.x).filter(Number.isFinite);
  const newMinX = (allX.length ? Math.min(...allX) : 0) - 200;
  const newMaxX = (allX.length ? Math.max(...allX) : 0) + NODE_WIDTH + 400;
  const newWidth = Math.max(newMaxX - newMinX, NODE_WIDTH + 400);

  const updatedSwimlaneNodes = swimlaneNodes.map((sw) => ({
    ...sw,
    position: { x: newMinX, y: sw.position.y },
    data: { ...sw.data, width: newWidth },
  }));

  return [...updatedSwimlaneNodes, ...updatedContentNodes];
}

function CombinedProcessFlowchartInner({ flowchartData, flowchartVersionId, processUuid }) {
  const base = useMemo(() => toCombinedProcessFlow(flowchartData), [flowchartData]);
  const [nodes, setNodes] = useState(base.nodes);
  const [layoutReady, setLayoutReady] = useState(false);
  const [subprocessLinks, setSubprocessLinks] = useState([]);
  const wrapperRef = useRef(null);
  const rfRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const versionId = String(flowchartVersionId || '').trim();
    if (!versionId) {
      setSubprocessLinks([]);
      return undefined;
    }
    fetchFlowchartSubprocessLinks(versionId)
      .then((rows) => {
        if (!cancelled) setSubprocessLinks(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSubprocessLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [flowchartVersionId]);

  useEffect(() => {
    setNodes(base.nodes);
    setLayoutReady(false);
    if (!base.needsElk) {
      setLayoutReady(true);
      return undefined;
    }
    let cancelled = false;
    applyElkLayout(base.nodes, base.edges, base.lanes)
      .then((laidOutNodes) => {
        if (!cancelled) {
          setNodes(laidOutNodes);
          setLayoutReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLayoutReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  const runFit = useCallback(
    (isPrint = false) => {
      if (!rfRef.current || nodes.length === 0) return;
      const contentNodes = nodes.filter((node) => node.type !== 'swimlane');
      rfRef.current.fitView({
        nodes: isPrint ? nodes : contentNodes.length > 0 ? contentNodes : nodes,
        padding: isPrint ? 0.04 : 0.18,
        minZoom: 0.01,
        maxZoom: 1,
        duration: 0,
      });
    },
    [nodes]
  );

  useEffect(() => {
    if (!layoutReady) return undefined;
    const el = wrapperRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.3)) {
          requestAnimationFrame(runFit);
        }
      },
      { threshold: [0.3, 0.6] }
    );
    io.observe(el);
    const timeoutId = window.setTimeout(() => runFit(false), 80);
    const onPrintFit = () => {
      window.dispatchEvent(new Event('resize'));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => runFit(true));
      });
    };
    const onScreenFit = () => runFit(false);
    window.addEventListener('beforeprint', onPrintFit);
    window.addEventListener('combined-flowchart-fit', onPrintFit);
    window.addEventListener('combined-flowchart-fit-screen', onScreenFit);
    return () => {
      io.disconnect();
      window.clearTimeout(timeoutId);
      window.removeEventListener('beforeprint', onPrintFit);
      window.removeEventListener('combined-flowchart-fit', onPrintFit);
      window.removeEventListener('combined-flowchart-fit-screen', onScreenFit);
    };
  }, [runFit, layoutReady]);

  return (
    <div ref={wrapperRef} className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1">
        <ReactFlow
          nodes={nodes}
          edges={base.edges}
          nodeTypes={combinedProcessDisplayNodeTypes}
          edgeTypes={combinedProcessEdgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll={false}
          preventScrolling={false}
          panOnScroll={false}
          fitView
          fitViewOptions={{ padding: 0.18, minZoom: 0.01, maxZoom: 1 }}
          minZoom={0.01}
          maxZoom={1.5}
          onInit={(instance) => {
            rfRef.current = instance;
            const contentNodes = nodes.filter((node) => node.type !== 'swimlane');
            instance.fitView({
              nodes: contentNodes.length > 0 ? contentNodes : nodes,
              padding: 0.18,
              minZoom: 0.01,
              maxZoom: 1,
              duration: 0,
            });
          }}
          proOptions={{ hideAttribution: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <Background color="#d1d5db" gap={20} variant="dots" />
          <FlowchartSubprocessLinkOverlay
            links={subprocessLinks}
            nodes={nodes}
            processUuid={processUuid}
          />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <FlowchartLegend />
    </div>
  );
}

/**
 * Standalone read-only process flowchart for Combined Flowchart page.
 * Visual parity with the actual flowchart page display — does not import or
 * modify FlowchartViewer.js. Not editable.
 */
export default function CombinedProcessFlowchartViewer({
  flowchartData,
  flowchartVersionId,
  processUuid,
}) {
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <CombinedProcessFlowchartInner
          flowchartData={flowchartData}
          flowchartVersionId={flowchartVersionId}
          processUuid={processUuid}
        />
      </ReactFlowProvider>
    </div>
  );
}
